// TIP-518 — three-up value props, scroll-driven.
// Left column: numbered editorial points that dim/brighten as the user
// scrolls. Right column: a sticky stage that crossfades between three
// TREVEX-native product mockups, one per point. Below 1100px the sticky
// column drops and each graphic inlines under its point.
//
// Ported from the homepage handoff (PromiseList + PromiseGraphic*).
import React, { useEffect, useRef, useState } from "react";
import Icon from "./Icon";

const PROMISE_POINTS = [
  {
    num: "01",
    title: "Every UAE business listed",
    body: "Every UAE business has a Listing on TREVEX. Free to claim. Free to manage.",
  },
  {
    num: "02",
    title: "Your profile, your terms",
    body: "Once claimed, the profile is yours to manage. Edit what is shown. Reply when asked.",
  },
  {
    num: "03",
    title: "Findable and up to date",
    body: "Search the directory. Find businesses you need. Follow them for changes.",
  },
];

/**
 * Given a list of bounding rects ({ top, height }) and the viewport centre,
 * return the index of the rect whose centre is closest to the viewport centre.
 * Exported for unit testing.
 */
export const computeActiveIndex = (rects, viewportCenter) => {
  let bestIdx = 0;
  let bestDist = Infinity;
  rects.forEach((rect, i) => {
    if (!rect) return;
    const elCenter = rect.top + rect.height / 2;
    const dist = Math.abs(elCenter - viewportCenter);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  });
  return bestIdx;
};

const DIRECTORY_TILES = [
  { name: "Keecs Trading FZ", sec: "Wholesale, Building", code: "DMCC", inits: "KT", color: "#EA5539" },
  { name: "Falcon Logistics FZE", sec: "Freight & Logistics", code: "JAFZA", inits: "FL", color: "#F59D15" },
  { name: "Madinat Holdings", sec: "Financial Services", code: "DIFC", inits: "MH", color: "#1F4D67" },
  { name: "Al Reem Industrial LLC", sec: "Industrial Supplies", code: "RAKEZ", inits: "AR", color: "#EA5539" },
  { name: "Bayan Capital Ltd", sec: "Investment Mgmt", code: "ADGM", inits: "BC", color: "#F59D15" },
  { name: "Crescent Trade Co.", sec: "General Trading", code: "DUBAI MAINLAND", inits: "CT", color: "#1F4D67" },
  { name: "Aurora Foods LLC", sec: "Food & Beverage", code: "SHARJAH FZ", inits: "AF", color: "#EA5539" },
  { name: "Stellar Tech Ltd", sec: "IT Services", code: "AJMAN FZ", inits: "ST", color: "#F59D15" },
];

const PromiseGraphicDirectory = ({ active }) => (
  <div className="pg pg-dir">
    <div className="pg-bar">
      <div className="pg-bar-l">
        <span className="pg-pulse" />
        <span>TREVEX Directory</span>
      </div>
      <span className="pg-bar-r">600,000+ businesses</span>
    </div>
    <div className="pg-search">
      <Icon name="search" size={14} />
      <span style={{ opacity: 0.55 }}>Search a business…</span>
    </div>
    <div className="pg-grid">
      {DIRECTORY_TILES.map((t, i) => (
        <div
          key={t.name}
          className={"pg-tile " + (active ? "is-in" : "")}
          style={{ "--d": i * 0.07 + "s" }}
        >
          <div className="pg-tile-av" style={{ background: t.color }}>{t.inits}</div>
          <div className="pg-tile-meta">
            <div className="pg-tile-name">{t.name}</div>
            <div className="pg-tile-sub">{t.code} · {t.sec}</div>
          </div>
          <span className="pg-tile-chip">○ Listing</span>
        </div>
      ))}
    </div>
  </div>
);

const PromiseGraphicProfile = ({ active }) => (
  <div className="pg pg-edit">
    <div className="pg-bar">
      <div className="pg-bar-l">
        <div className="pg-tile-av" style={{ background: "#EA5539", width: 30, height: 30, fontSize: 10 }}>KT</div>
        <div style={{ lineHeight: 1.1 }}>
          <div style={{ color: "#fff", fontSize: 12.5, fontWeight: 600 }}>Keecs Trading FZ</div>
          <div className="pg-tile-sub">DMCC · Self-managed</div>
        </div>
      </div>
      <span className="pg-chip-gold">Claimed</span>
    </div>

    <div className="pg-rows">
      <div className="pg-row">
        <span className="pg-row-label">Registered address</span>
        <span className="pg-row-val">Unit 2106, JLT Cluster R</span>
        <span className="pg-row-edit"><Icon name="edit" size={11} /></span>
      </div>
      <div className={"pg-row pg-row--edit " + (active ? "is-on" : "")}>
        <span className="pg-row-label">Primary sector</span>
        <span className="pg-row-input">
          Wholesale, Building Material<span className="pg-cursor" />
        </span>
        <div className="pg-row-actions">
          <button className="pg-btn-save">Save</button>
          <button className="pg-btn-cancel">Cancel</button>
        </div>
      </div>
      <div className="pg-row">
        <span className="pg-row-label">Banking partner</span>
        <span className="pg-row-val">Emirates NBD</span>
        <span className="pg-row-edit"><Icon name="edit" size={11} /></span>
      </div>
    </div>

    <div className="pg-reply">
      <div className="pg-reply-meta">From Falcon Logistics · 2 days ago</div>
      <div className="pg-reply-msg">"Are you open to mainland deliveries above 5 tonnes?"</div>
      <div className="pg-reply-input">
        <span className="pg-reply-typing">Yes, mainland deliveries are available<span className="pg-cursor" /></span>
        <button className="pg-btn-send">Reply <Icon name="arrow-right" size={11} /></button>
      </div>
    </div>
  </div>
);

const PromiseGraphicSearch = ({ active }) => (
  <div className="pg pg-find">
    <div className="pg-find-search">
      <Icon name="search" size={15} />
      <span className="pg-find-query">industrial supplies</span>
      <span className="pg-cursor" />
    </div>
    <div className="pg-find-meta">
      <span>3 matches in UAE</span>
      <span>Sort · Verified first</span>
    </div>
    <div className="pg-find-results">
      <div className={"pg-result " + (active ? "is-in" : "")} style={{ "--d": "0.05s" }}>
        <div className="pg-tile-av" style={{ background: "#EA5539" }}>AR</div>
        <div className="pg-tile-meta">
          <div className="pg-tile-name">Al Reem Industrial Supplies LLC</div>
          <div className="pg-tile-sub">RAKEZ · Wholesale</div>
        </div>
        <span className="pg-result-chip verified">✓ Verified</span>
      </div>
      <div className={"pg-result " + (active ? "is-in" : "")} style={{ "--d": "0.14s" }}>
        <div className="pg-tile-av" style={{ background: "#F59D15" }}>NI</div>
        <div className="pg-tile-meta">
          <div className="pg-tile-name">Northern Industrial Co.</div>
          <div className="pg-tile-sub">DMCC · Distribution</div>
        </div>
        <span className="pg-result-chip claimed">○ Claimed</span>
      </div>
      <div className={"pg-result pg-result--followed " + (active ? "is-in" : "")} style={{ "--d": "0.23s" }}>
        <div className="pg-tile-av" style={{ background: "#1F4D67" }}>FL</div>
        <div className="pg-tile-meta">
          <div className="pg-tile-name">Falcon Logistics FZE</div>
          <div className="pg-tile-sub">JAFZA · Freight & Logistics</div>
        </div>
        <button className="pg-follow"><Icon name="bell" size={11} /> Following</button>
      </div>
    </div>

    <div className={"pg-notif " + (active ? "is-in" : "")}>
      <span className="pg-notif-dot" />
      <div>
        <div className="pg-notif-title">Falcon Logistics FZE</div>
        <div className="pg-notif-body">Registered address updated to JLT</div>
      </div>
      <span className="pg-notif-time">2h</span>
    </div>
  </div>
);

const GRAPHICS = [PromiseGraphicDirectory, PromiseGraphicProfile, PromiseGraphicSearch];

const ValueProps = () => {
  const [active, setActive] = useState(0);
  const r0 = useRef(null);
  const r1 = useRef(null);
  const r2 = useRef(null);
  const refs = [r0, r1, r2];

  useEffect(() => {
    let raf = 0;
    const compute = () => {
      raf = 0;
      const rects = refs.map((r) => {
        const el = r.current;
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        return { top: rect.top, height: rect.height };
      });
      setActive(computeActiveIndex(rects, window.innerHeight / 2));
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section data-component="TrevexBenefits" className="section-dark promise-section">
      <div className="container-wide">
        <div className="promise-layout">
          <div className="promise-text">
            {PROMISE_POINTS.map((p, i) => {
              const Graphic = GRAPHICS[i];
              return (
                <div
                  key={p.num}
                  ref={refs[i]}
                  className={"promise-point " + (active === i ? "is-active" : "")}
                >
                  <div className="promise-num">{p.num}</div>
                  <div className="promise-copy">
                    <h3 className="promise-title">{p.title}</h3>
                    <p className="promise-body">{p.body}</p>
                    {/* Inline graphic — only visible on narrow viewports. */}
                    <div className="promise-inline-graphic">
                      <Graphic active />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="promise-visual">
            <div className="promise-visual-inner">
              {GRAPHICS.map((Graphic, i) => (
                <div key={i} className={"promise-graphic " + (active === i ? "is-active" : "")}>
                  <Graphic active={active === i} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValueProps;
